import { FeatureFlag } from '@signup/shared';
import { isFeatureEnabled } from './configurations';

/**
 * # Mutiny Experience Tracking System
 *
 * ## Overview
 * This module provides comprehensive tracking and logging of Mutiny experience information across
 * the signup application. It captures which Mutiny experiences/experiments a user is seeing and
 * includes this information in telemetry events, API request headers, and create trial requests.
 *
 * ## Key Concepts
 *
 * ### Multiple Experience Support
 * The system is designed to handle multiple concurrent Mutiny experiences rather than a single
 * "primary" experience. This allows for comprehensive tracking of all active experiments affecting
 * a user's signup journey.
 *
 * ### Data Sources
 * Mutiny exposes experience information through `window.mutiny.experiences` - an array of
 * experience objects containing:
 * - `audienceSegment`: The audience segment the user belongs to
 * - `experience`: The name/description of the experience
 * - `impressionType`: Type of impression ("control", "treatment", etc.)
 * - `page`: The page URL where the experience is active
 * - `variationKey`: Unique identifier for the variation
 * - `variationName`: Human-readable name of the variation
 *
 * ## Integration Points
 *
 * ### 1. Telemetry Enhancement
 * All telemetry events automatically include Mutiny experience data via `SignupLogger.tsx`
 *
 * ### 2. API Request Headers
 * All API requests include Mutiny headers via `signupApi.ts`:
 * - `x-mutiny-experience-count`: Number of active experiences
 * - `x-mutiny-experiences`: JSON-encoded array of all experiences
 *
 * ### 3. Create Trial API Payload
 * Signup requests include comprehensive Mutiny data:
 * - `mutinyExperienceCount`: Total number of experiences
 * - `mutinyExperiences`: JSON string containing all experience details
 *
 * ### 4. Server-Side Logging
 * Backend automatically logs Mutiny information from headers and payload data
 *
 * ## Usage Examples
 *
 * ```typescript
 * // Get current Mutiny experiences (synchronous)
 * const data = MutinyExperience.getMutinyExperienceData();
 * console.log(`User has ${data.experienceCount} active experiences`);
 *
 * // Wait for Mutiny data to load (asynchronous)
 * const data = await MutinyExperience.waitForMutinyData(5000);
 *
 * // Get formatted data for logging
 * const loggingData = MutinyExperience.getExperienceSummaryForLogging();
 * ```
 *
 * ## Performance Considerations
 * - Data is cached after first access to avoid repeated DOM queries
 * - Async loading with configurable timeout prevents blocking
 * - Experience filtering by current page URL reduces data volume
 *
 * ## Testing Support
 * The system includes mock detection for integration tests, allowing reliable testing
 * of Mutiny experience tracking without requiring actual Mutiny script presence.
 */

export interface MutinyExperienceData {
  audienceSegment?: string;
  experience?: string;
  impressionType?: string;
  page?: string;
  variationKey?: string;
  variationName?: string;
}

export interface MutinyExperiencesSummary {
  experiences: MutinyExperienceData[];
  experienceCount: number;
}

export interface MutinyLoggingData {
  mutinyExperienceCount: number;
  mutinyExperiences: Array<{
    experience?: string;
    variationKey?: string;
    variationName?: string;
    impressionType?: string;
    audienceSegment?: string;
  }>;
}

declare global {
  interface Window {
    mutiny?: {
      experiences?: MutinyExperienceData[];
      client?: {
        _queue?: Record<string, unknown>;
        identify?: (...args: unknown[]) => Promise<unknown>;
        trackConversion?: (...args: unknown[]) => Promise<unknown>;
        defaultOptOut?: (...args: unknown[]) => void;
        optOut?: (...args: unknown[]) => void;
        optIn?: (...args: unknown[]) => void;
      };
    };
  }
}

export class MutinyExperience {
  static getMutinyExperienceData(): MutinyExperiencesSummary {
    // Check if Mutiny experience tracking is enabled via feature flag
    if (!isFeatureEnabled(FeatureFlag.FF_ENABLE_MUTINY_EXPERIENCE_TRACKING)) {
      return {
        experiences: [],
        experienceCount: 0,
      };
    }

    // Check if there's a test mock available on window
    if (
      typeof window !== 'undefined' &&
      (window as any).MutinyExperience?.getMutinyExperienceData &&
      (window as any).MutinyExperience !== this
    ) {
      console.log('Using MutinyExperience mock from window for getMutinyExperienceData');
      return (window as any).MutinyExperience.getMutinyExperienceData();
    }

    const summary: MutinyExperiencesSummary = {
      experiences: [],
      experienceCount: 0,
    };

    // Check window.mutiny.experiences array
    if (window.mutiny?.experiences && Array.isArray(window.mutiny.experiences)) {
      summary.experiences = window.mutiny.experiences.filter(exp => {
        // Filter for current page or all pages
        return (
          exp.page === window.location.href ||
          exp.page === window.location.origin + window.location.pathname
        );
      });

      summary.experienceCount = summary.experiences.length;
    }

    return summary;
  }

  static waitForMutinyData(timeout: number = 5000): Promise<MutinyExperiencesSummary> {
    // Check if Mutiny experience tracking is enabled via feature flag
    if (!isFeatureEnabled(FeatureFlag.FF_ENABLE_MUTINY_EXPERIENCE_TRACKING)) {
      return Promise.resolve({
        experiences: [],
        experienceCount: 0,
      });
    }

    // Check if there's a test mock available on window
    if (
      typeof window !== 'undefined' &&
      (window as any).MutinyExperience?.waitForMutinyData &&
      (window as any).MutinyExperience !== this
    ) {
      console.log('Using MutinyExperience mock from window');
      return (window as any).MutinyExperience.waitForMutinyData(timeout);
    }

    return new Promise(resolve => {
      const startTime = Date.now();

      const checkForData = () => {
        const data = this.getMutinyExperienceData();

        if (data.experienceCount > 0 || Date.now() - startTime > timeout) {
          resolve(data);
          return;
        }

        setTimeout(checkForData, 100);
      };

      checkForData();
    });
  }

  // Helper method to get a flattened summary for logging
  static getExperienceSummaryForLogging(): MutinyLoggingData {
    const data = this.getMutinyExperienceData();

    return {
      mutinyExperienceCount: data.experienceCount,
      mutinyExperiences: data.experiences.map(exp => ({
        experience: exp.experience,
        variationKey: exp.variationKey,
        variationName: exp.variationName,
        impressionType: exp.impressionType,
        audienceSegment: exp.audienceSegment,
      })),
    };
  }
}
